import {
    ArrowPathIcon,
    ExclamationTriangleIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import React from 'react';

/**
 * The loading / error / empty blocks the resource pages share. Previously each
 * page in each SPA carried its own copy with slightly different copy and
 * spacing; the redesign uses one set so the sections read as one product.
 *
 * All three sit in the app's standard card, since the pages themselves are on
 * the gray page background.
 */
const CARD_CLASS =
    'bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col items-center justify-center text-center';

export const ResourceLoading: React.FC<{ label?: string }> = ({ label = 'Loading…' }) => (
    <div className={`${CARD_CLASS} py-20 gap-3`}>
        <span className="h-6 w-6 border-2 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
        <span className="text-sm text-gray-500">{label}</span>
    </div>
);

interface ResourceErrorProps {
    message: string;
    onRetry: () => void;
}

export const ResourceError: React.FC<ResourceErrorProps> = ({ message, onRetry }) => (
    <div className={`${CARD_CLASS} py-20 px-8`}>
        <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mb-4" />
        <h3 className="text-lg font-bold text-gray-800 mb-2">Something went wrong</h3>
        <p className="text-sm text-gray-600 mb-5 max-w-md">{message}</p>
        <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-2 bg-gray-800 text-white px-6 py-2 rounded-xl shadow hover:bg-gray-900 transition-colors font-semibold text-sm"
        >
            <ArrowPathIcon className="w-4 h-4" />
            Retry
        </button>
    </div>
);

interface ResourceEmptyProps {
    title: string;
    description: string;
}

export const ResourceEmpty: React.FC<ResourceEmptyProps> = ({ title, description }) => (
    <div className={`${CARD_CLASS} py-20 px-8`}>
        <MagnifyingGlassIcon className="w-12 h-12 text-gray-300 mb-4" />
        <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 max-w-md">{description}</p>
    </div>
);
